/**
 * TodaysCaseCard Component
 * 
 * Case source selection and context configuration for daily training.
 * Allows users to choose between quick cases and real work, configure
 * context packs, and edit the case prompt.
 */

import React from 'react';
import { Zap, Briefcase, FileText, Shield, BookOpen, AlertTriangle } from 'lucide-react';
import type { TrainingCase, CaseSource, ContextPack } from '../../types/trainingTypes';

interface TodaysCaseCardProps {
    trainingCase: TrainingCase;
    onSourceChange: (source: CaseSource) => void;
    onContextPackChange: (pack: ContextPack) => void;
    onTitleChange: (title: string) => void;
    onPromptChange: (prompt: string) => void;
    onGenerateDrafts: () => void;
    isGenerating: boolean;
}

export const TodaysCaseCard: React.FC<TodaysCaseCardProps> = ({
    trainingCase,
    onSourceChange,
    onContextPackChange,
    onTitleChange,
    onPromptChange,
    onGenerateDrafts,
    isGenerating
}) => {
    const toggleContextPack = (key: keyof ContextPack) => {
        onContextPackChange({
            ...trainingCase.contextPack,
            [key]: !trainingCase.contextPack[key]
        });
    };

    return (
        <div className="card-elevated p-6 space-y-6" style={{ background: '#111827', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 12 }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-hig-text-primary">
                    Today's Training
                </h2>
                <span className="text-xs text-hig-text-tertiary uppercase tracking-wide">
                    {new Date().toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            </div>

            {/* A. Case Source — styled as proper tabs (Fix 5 Row 1) */}
            <div className="space-y-3">
                <div className="flex" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
                    <SourceTab
                        selected={trainingCase.source === 'quick_case'}
                        onClick={() => onSourceChange('quick_case')}
                        icon={<Zap className="w-4 h-4" />}
                        label="Quick Case"
                    />
                    <SourceTab
                        selected={trainingCase.source === 'from_work'}
                        onClick={() => onSourceChange('from_work')}
                        icon={<Briefcase className="w-4 h-4" />}
                        label="From My Work"
                    />
                </div>
            </div>

            {/* B. Context Pack filters + Generate button row (Fix 1 + Fix 5 Row 2) */}
            <div className="space-y-2">
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                    Context
                </span>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <ContextChip
                            active={trainingCase.contextPack.toneGuide}
                            onClick={() => toggleContextPack('toneGuide')}
                            icon={<FileText className="w-3.5 h-3.5" />}
                            label="Tone Guide"
                        />
                        <ContextChip
                            active={trainingCase.contextPack.policies}
                            onClick={() => toggleContextPack('policies')}
                            icon={<Shield className="w-3.5 h-3.5" />}
                            label="Policies"
                        />
                        <ContextChip
                            active={trainingCase.contextPack.pastExamples}
                            onClick={() => toggleContextPack('pastExamples')}
                            icon={<BookOpen className="w-3.5 h-3.5" />}
                            label="Past Examples"
                        />
                        <ContextChip
                            active={trainingCase.contextPack.redZones}
                            onClick={() => toggleContextPack('redZones')}
                            icon={<AlertTriangle className="w-3.5 h-3.5" />}
                            label="Red Zones"
                        />
                    </div>
                    {/* Generate Drafts — right-aligned, auto width (Fix 1) */}
                    <button
                        onClick={onGenerateDrafts}
                        disabled={isGenerating || !trainingCase.prompt.trim()}
                        style={{
                            width: 'auto',
                            padding: '10px 24px',
                            background: 'var(--accent)',
                            color: '#0F172A',
                            fontWeight: 700,
                            fontSize: 13,
                            borderRadius: 8,
                            border: 'none',
                            cursor: (isGenerating || !trainingCase.prompt.trim()) ? 'not-allowed' : 'pointer',
                            opacity: (isGenerating || !trainingCase.prompt.trim()) ? 0.5 : 1,
                            transition: 'opacity 0.2s ease',
                            flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { if (!isGenerating) (e.currentTarget.style.opacity = '0.88'); }}
                        onMouseLeave={(e) => { (e.currentTarget.style.opacity = (isGenerating || !trainingCase.prompt.trim()) ? '0.5' : '1'); }}
                    >
                        {isGenerating ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 14, height: 14, border: '2px solid #0F172A', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                                Generating...
                            </span>
                        ) : (
                            'Generate Drafts'
                        )}
                    </button>
                </div>
            </div>

            {/* C. Case Content */}
            <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                    <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        Case Title
                    </label>
                    <input
                        type="text"
                        value={trainingCase.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        style={{
                            width: '100%', padding: '10px 14px',
                            background: 'rgba(255,255,255,0.04)',
                            border: '0.5px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, color: 'rgba(255,255,255,0.8)',
                            fontSize: 13, outline: 'none',
                        }}
                        placeholder="Enter case title..."
                    />
                </div>

                {/* Prompt */}
                <div className="space-y-2">
                    <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        Task Prompt
                    </label>
                    <textarea
                        value={trainingCase.prompt}
                        onChange={(e) => onPromptChange(e.target.value)}
                        style={{
                            width: '100%', minHeight: 120, padding: '10px 14px',
                            background: 'rgba(255,255,255,0.04)',
                            border: '0.5px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, color: 'rgba(255,255,255,0.8)',
                            fontSize: 13, outline: 'none', resize: 'vertical',
                            fontFamily: 'inherit',
                        }}
                        placeholder="Describe the task your Replica should handle..."
                    />
                </div>
            </div>
        </div>
    );
};

// ============================================
// SOURCE TAB — proper tab style (Fix 5)
// ============================================

interface SourceTabProps {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
}

const SourceTab: React.FC<SourceTabProps> = ({ selected, onClick, icon, label }) => {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px',
                background: selected ? 'rgba(0,229,255,0.08)' : 'transparent',
                borderBottom: selected ? '1.5px solid var(--accent)' : '1.5px solid transparent',
                color: selected ? 'var(--accent)' : 'rgba(255,255,255,0.4)',
                fontWeight: selected ? 600 : 400,
                fontSize: 13, cursor: 'pointer',
                border: 'none', borderBottomStyle: 'solid',
                borderBottomWidth: 1.5,
                borderBottomColor: selected ? 'var(--accent)' : 'transparent',
                transition: 'all 0.2s ease',
            }}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
};

// ============================================
// CONTEXT CHIP — filter chip style (Fix 5)
// ============================================

interface ContextChipProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
}

const ContextChip: React.FC<ContextChipProps> = ({ active, onClick, icon, label }) => {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 12px',
                background: active ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.04)',
                border: `0.5px solid ${active ? 'rgba(0,229,255,0.3)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 6,
                color: active ? 'var(--accent)' : 'rgba(255,255,255,0.45)',
                fontSize: 12, cursor: 'pointer',
                transition: 'all 0.15s ease',
            }}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
};

export default TodaysCaseCard;
